// src/services/publicApi.js
//
// Unauthenticated read-only API for the public marketing/browse site.
// Mirrors the mobile app's home + book detail, but exposes only safe
// book/list metadata (no auth, no child data, no personalization).
import { API_BASE_URL, API_BASE_URL_CANDIDATES } from '../lib/apiBaseUrl';

async function getJson(path) {
  let lastError;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await fetch(`${baseUrl}${path}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    } catch (error) {
      lastError = error;
      if (!import.meta.env.DEV) break;
      console.warn(`[publicApi] ${baseUrl}${path} failed:`, error);
    }
  }

  throw lastError;
}

export const publicApi = {
  // All homepage-visible curated lists, each with a preview of its books.
  getCuratedLists: async ({ limit, sort } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (sort) params.set('sort', sort);
    const queryString = params.toString();
    const data = await getJson(`/public/curated-lists${queryString ? `?${queryString}` : ''}`);
    return Array.isArray(data?.lists) ? data.lists : [];
  },

  // A single curated list with all of its books.
  getCuratedList: (id) => getJson(`/public/curated-lists/${id}`),

  // Latest public New Releases row, backed by editorial_picks.
  getNewReleases: async ({ limit } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    const queryString = params.toString();
    return getJson(`/public/new-releases${queryString ? `?${queryString}` : ''}`);
  },

  // Public book detail (accepts internal id, isbn13, isbn10, asin, google id).
  getBook: (id) => getJson(`/books/${encodeURIComponent(id)}`),

  // Public reviews for a book.
  getBookReviews: async (id) => {
    const data = await getJson(`/books/${encodeURIComponent(id)}/reviews`);
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.reviews) ? data.reviews : [];
  },
};

export { API_BASE_URL };
